import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Save, X, Search, Image as ImageIcon } from 'lucide-react';
import useConfirmDialog from '../hooks/useConfirmDialog';
import './AdminCVTemplateManagement.scss';

interface CVTemplate {
  id: number;
  category: string;
  value: string;
  image_url?: string | null;
  parent_value?: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminCVTemplateManagement: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const [templates, setTemplates] = useState<{ [category: string]: CVTemplate[] }>({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<CVTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState({ category: '', value: '', parent_value: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'admin') {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  // Redirect if not authenticated or not an admin
  if (!isAuthenticated || user?.user_type !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/cv-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setTemplates(result.data);
        
        // Set first category as selected if none selected
        if (!selectedCategory && Object.keys(result.data).length > 0) {
          setSelectedCategory(Object.keys(result.data)[0]);
        }
      } else {
        console.error('Failed to fetch CV templates');
      }
    } catch (error) {
      console.error('Error fetching CV templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedPositions = async () => {
    if (!await confirm('Ar tikrai norite importuoti pareigybių duomenis? Esami darbo sričių, pareigybių tipų ir pareigybių įrašai bus deaktyvuoti ir pakeisti naujais.')) return;
    setSeeding(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/cv-templates/seed-positions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const result = await response.json();
        alert(`Sėkmingai importuota: ${result.workFields} darbo sričių, ${result.positionTypes} pareigybių tipų, ${result.positions} pareigybių`);
        fetchTemplates();
      } else {
        const err = await response.json();
        alert(`Klaida: ${err.message}`);
      }
    } catch (error) {
      alert('Nepavyko importuoti pareigybių duomenų');
    } finally {
      setSeeding(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.category || !newTemplate.value) return;

    try {
      const token = localStorage.getItem('auth_token');
      const payload: any = {
        category: newTemplate.category,
        value: newTemplate.value
      };
      if (newTemplate.parent_value) {
        payload.parent_value = newTemplate.parent_value;
      }
      const response = await fetch(`${API_URL}/cv-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setNewTemplate({ category: '', value: '', parent_value: '' });
        setShowAddForm(false);
        fetchTemplates();
      } else {
        console.error('Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  const handleUpdateTemplate = async (template: CVTemplate) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/cv-templates/${template.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          value: template.value,
          display_order: template.display_order,
          parent_value: template.parent_value || null
        })
      });

      if (response.ok) {
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        console.error('Failed to update template');
      }
    } catch (error) {
      console.error('Error updating template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    if (!await confirm('Ar tikrai norite ištrinti šį šabloną?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/cv-templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchTemplates();
      } else {
        console.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleImageUpload = async (templateId: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/cv-templates/${templateId}/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        fetchTemplates();
      } else {
        console.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleImageDelete = async (templateId: number) => {
    if (!await confirm('Ar tikrai norite pašalinti paveikslėlį?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/cv-templates/${templateId}/image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchTemplates();
      } else {
        console.error('Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const getFullImageUrl = (imageUrl: string): string => {
    if (imageUrl.startsWith('http')) return imageUrl;
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
    return `${baseUrl}${imageUrl}`;
  };

  const getCategoryDisplayName = (category: string): string => {
    const categoryNames: { [key: string]: string } = {
      education_level: 'Išsilavinimo lygiai',
      field_of_study: 'Studijų sritys',
      work_location: 'Darbo vietos',
      work_field: 'Darbo sritys',
      work_type: 'Darbo tipai',
      work_time: 'Darbo laikai',
      language: 'Kalbos',
      position: 'Pareigos',
      position_type: 'Pareigybių tipai',
      institution: 'Mokymo įstaigos',
      city: 'Miestai',
      computer_skill_office: 'Biuro programos',
      computer_skill_accounting: 'Apskaitos programos',
      computer_skill_programming: 'Programavimo kalbos',
      computer_skill_os: 'Operacinės sistemos',
      computer_skill_database: 'Duomenų bazės',
      computer_skill_design: 'Dizaino programos',
      job_preference_position: 'Pageidaujamos pareigos',
      company_activity: 'Įmonės veiklos sritys',
      degree: 'Laipsnis',
      specialization: 'Specialybės (aukštasis/aukštesnysis)',
      professional_qualification: 'Profesinės kvalifikacijos'
    };
    return categoryNames[category] || category;
  };

  const filteredTemplates = selectedCategory && templates[selectedCategory] 
    ? templates[selectedCategory].filter(template =>
        template.value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  if (loading) {
    return (
      <div className="admin-cv-template-management">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Kraunami CV šablonai...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-cv-template-management">
      {ConfirmDialogNode}
      <div className="page-header">
        <h1>CV šablonų valdymas</h1>
        <p>Valdykite CV formos pasirinkimo variantus</p>
        <button
          className="seed-positions-btn"
          onClick={handleSeedPositions}
          disabled={seeding}
        >
          {seeding ? 'Importuojama...' : 'Importuoti pareigybių duomenis'}
        </button>
      </div>

      <div className="template-management-content">
        {/* Category Selector */}
        <div className="category-selector">
          <h3>Kategorijos</h3>
          <div className="category-list">
            {Object.keys(templates).map(category => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {getCategoryDisplayName(category)}
                <span className="count">({templates[category].length})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Template Management */}
        <div className="template-content">
          {selectedCategory && (
            <>
              {/* Search and Add Bar */}
              <div className="template-toolbar">
                <div className="search-box">
                  <Search size={20} />
                  <input
                    type="text"
                    placeholder="Ieškoti šablonų..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button 
                  className="add-template-btn"
                  onClick={() => {
                    setShowAddForm(true);
                    setNewTemplate({ category: selectedCategory, value: '', parent_value: '' });
                  }}
                >
                  <Plus size={16} />
                  Pridėti naują
                </button>
              </div>

              {/* Add New Template Form */}
              {showAddForm && (
                <div className="add-template-form">
                  <h4>Pridėti naują šabloną</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Kategorija</label>
                      <select
                        value={newTemplate.category}
                        onChange={(e) => setNewTemplate(prev => ({ ...prev, category: e.target.value }))}
                      >
                        <option value="">Pasirinkite kategoriją</option>
                        {Object.keys(templates).map(category => (
                          <option key={category} value={category}>
                            {getCategoryDisplayName(category)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Reikšmė</label>
                      <input
                        type="text"
                        value={newTemplate.value}
                        onChange={(e) => setNewTemplate(prev => ({ ...prev, value: e.target.value }))}
                        placeholder="Įveskite reikšmę"
                      />
                    </div>
                    {newTemplate.category === 'position' && (
                      <div className="form-group">
                        <label>Darbo sritis (parent)</label>
                        <select
                          value={newTemplate.parent_value}
                          onChange={(e) => setNewTemplate(prev => ({ ...prev, parent_value: e.target.value }))}
                        >
                          <option value="">-- Nepriskirta --</option>
                          {(templates['work_field'] || []).map(wf => (
                            <option key={wf.id} value={wf.value}>{wf.value}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="form-actions">
                      <button className="save-btn" onClick={handleCreateTemplate}>
                        <Save size={16} />
                        Išsaugoti
                      </button>
                      <button className="cancel-btn" onClick={() => setShowAddForm(false)}>
                        <X size={16} />
                        Atšaukti
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Templates List */}
              <div className="templates-section">
                <div className="section-header">
                  <h3>{getCategoryDisplayName(selectedCategory)}</h3>
                  <span className="template-count">{filteredTemplates.length} šablonai</span>
                </div>

                {filteredTemplates.length === 0 ? (
                  <div className="empty-state">
                    <p>Šablonų nerasta</p>
                  </div>
                ) : (
                  <div className="templates-list">
                    {filteredTemplates.map((template) => (
                      <div key={template.id} className="template-item">
                        {editingTemplate?.id === template.id ? (
                          <div className="template-edit-form">
                            <input
                              type="text"
                              value={editingTemplate.value}
                              onChange={(e) => setEditingTemplate(prev => 
                                prev ? { ...prev, value: e.target.value } : null
                              )}
                              className="edit-input"
                            />
                            {selectedCategory === 'position' && (
                              <select
                                value={editingTemplate.parent_value || ''}
                                onChange={(e) => setEditingTemplate(prev =>
                                  prev ? { ...prev, parent_value: e.target.value || null } : null
                                )}
                                className="edit-select"
                              >
                                <option value="">-- Darbo sritis --</option>
                                {(templates['work_field'] || []).map(wf => (
                                  <option key={wf.id} value={wf.value}>{wf.value}</option>
                                ))}
                              </select>
                            )}
                            <div className="edit-actions">
                              <button 
                                className="save-btn"
                                onClick={() => handleUpdateTemplate(editingTemplate)}
                              >
                                <Save size={14} />
                              </button>
                              <button 
                                className="cancel-btn"
                                onClick={() => setEditingTemplate(null)}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="template-display">
                            <div className="template-info">
                              <span className="template-value">{template.value}</span>
                              {selectedCategory === 'position' && template.parent_value && (
                                <span className="template-parent">Sritis: {template.parent_value}</span>
                              )}
                              <span className="template-order">Eilė: {template.display_order}</span>
                            </div>
                            <div className="template-actions">
                              {template.image_url ? (
                                <div className="image-preview-inline">
                                  <img src={getFullImageUrl(template.image_url)} alt={template.value} />
                                  <button
                                    className="remove-image-btn"
                                    onClick={() => handleImageDelete(template.id)}
                                    title="Pašalinti paveikslėlį"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ) : (
                                <label className="image-btn">
                                  <ImageIcon size={14} />
                                  <span>Paveikslėlis</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(template.id, file);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              )}
                              <button 
                                className="edit-btn"
                                onClick={() => setEditingTemplate(template)}
                              >
                                <Edit size={14} />
                                <span>Redaguoti</span>
                              </button>
                              <button 
                                className="delete-btn"
                                onClick={() => handleDeleteTemplate(template.id)}
                              >
                                <Trash2 size={14} />
                                <span>Ištrinti</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCVTemplateManagement;
